import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, RotateCcw, Save } from "lucide-react";
import { DIRECTIONS } from "@/lib/direction";
import {
  FONT_OPTIONS,
  cssVarsForTheme,
  withThemeDefaults,
  themeSettingsQueryOptions,
  type FontKey,
  type ThemeId,
  type ThemeSettings,
} from "@/lib/theme.queries";
import { saveThemeSettings } from "@/lib/theme.functions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/admin/theme")({
  component: ThemeAdmin,
});

/** Field descriptor: a dotted path into ThemeSettings plus its label. */
type ColorField = { path: [keyof ThemeSettings, string]; label: string; hint?: string };

const CONTENT_FIELDS: ColorField[] = [
  { path: ["content", "background"], label: "Content background" },
  { path: ["content", "foreground"], label: "Text color" },
  { path: ["content", "muted"], label: "Muted surface", hint: "subtle fills, table stripes" },
  { path: ["content", "mutedForeground"], label: "Muted text", hint: "captions, secondary copy" },
];

const TOPBAR_FIELDS: ColorField[] = [
  { path: ["topbar", "background"], label: "Top bar background" },
  { path: ["topbar", "foreground"], label: "Top bar text" },
];

const HIGHLIGHT_FIELDS: ColorField[] = [
  { path: ["highlight", "primary"], label: "Primary", hint: "main buttons, badges" },
  { path: ["highlight", "link"], label: "Link", hint: "inline text links (independent of buttons)" },
  { path: ["highlight", "primaryForeground"], label: "Primary text", hint: "text on primary" },
  { path: ["highlight", "accent"], label: "Accent", hint: "highlights, tags" },
  { path: ["highlight", "accentForeground"], label: "Accent text" },
  { path: ["highlight", "brand"], label: "Brand", hint: "gradient start, wordmark" },
  { path: ["highlight", "brandForeground"], label: "Brand text" },
  { path: ["highlight", "brand2"], label: "Brand secondary", hint: "aurora wash, utilities" },
  { path: ["highlight", "gradientEnd"], label: "Gradient end", hint: "brand strip's second stop" },
  { path: ["highlight", "ring"], label: "Focus ring" },
];

const SURFACE_FIELDS: ColorField[] = [
  { path: ["surfaces", "card"], label: "Card background" },
  { path: ["surfaces", "cardForeground"], label: "Card text" },
  { path: ["surfaces", "secondary"], label: "Secondary surface", hint: "chips, hovers" },
  { path: ["surfaces", "secondaryForeground"], label: "Secondary text" },
  { path: ["surfaces", "border"], label: "Borders & inputs" },
];

function getColor(s: ThemeSettings, [group, key]: ColorField["path"]): string {
  return (s[group] as Record<string, string>)[key];
}

function setColor(s: ThemeSettings, [group, key]: ColorField["path"], value: string): ThemeSettings {
  return { ...s, [group]: { ...(s[group] as Record<string, string>), [key]: value } };
}

/** Normalize #rgb → #rrggbb; returns null when the text isn't a color. */
function normalizeHex(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  const m3 = /^#?([0-9a-f]{3})$/.exec(t);
  if (m3) return `#${m3[1].split("").map((c) => c + c).join("")}`;
  const m6 = /^#?([0-9a-f]{6})$/.exec(t);
  return m6 ? `#${m6[1]}` : null;
}

function ThemeAdmin() {
  const { data, isLoading } = useQuery(themeSettingsQueryOptions());
  const qc = useQueryClient();

  const [editing, setEditing] = useState<ThemeId | null>(null);
  const [draft, setDraft] = useState<ThemeSettings | null>(null);
  const [baseline, setBaseline] = useState<ThemeSettings | null>(null);
  const [makeDefault, setMakeDefault] = useState(false);

  const active = data?.active ?? "v1";
  const themeId: ThemeId = editing ?? active;
  const serverRow = data?.themes.find((t) => t.theme_id === themeId);

  // Seed the draft lazily: first render after data arrives, or after a theme
  // switch. Baseline is "the values loaded at open" that Revert restores.
  if (data && serverRow && (draft === null || editing === null)) {
    if (editing === null) setEditing(active);
    if (draft === null) {
      const seeded = withThemeDefaults(themeId, serverRow.settings);
      setDraft(seeded);
      setBaseline(seeded);
      setMakeDefault(themeId === active);
    }
  }

  const dirty = useMemo(() => {
    if (!draft || !baseline) return false;
    const contentChanged = JSON.stringify(draft) !== JSON.stringify(baseline);
    const defaultChanged = makeDefault !== (themeId === active);
    return contentChanged || defaultChanged;
  }, [draft, baseline, makeDefault, themeId, active]);

  const switchTheme = (next: ThemeId) => {
    if (next === themeId) return;
    if (dirty && !confirm(`Discard unsaved changes to ${label(themeId)}?`)) return;
    const row = data?.themes.find((t) => t.theme_id === next);
    if (!row) return;
    setEditing(next);
    const seeded = withThemeDefaults(next, row.settings);
    setDraft(seeded);
    setBaseline(seeded);
    setMakeDefault(next === active);
  };

  const saveMut = useMutation({
    mutationFn: () =>
      saveThemeSettings({
        data: {
          theme_id: themeId,
          settings: draft!,
          ...(makeDefault && themeId !== active ? { make_default: true } : {}),
        },
      }),
    onSuccess: (res) => {
      setBaseline(draft);
      setMakeDefault(themeId === res.active);
      qc.invalidateQueries({ queryKey: ["theme-settings"] });
      toast.success(
        res.active === themeId
          ? `${label(themeId)} saved — live for all visitors`
          : `${label(themeId)} saved`,
      );
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  if (isLoading || !draft || !baseline) {
    return <p className="text-muted-foreground">Loading theme settings…</p>;
  }

  const colorRow = (f: ColorField) => (
    <ColorRow
      key={f.path.join(".")}
      label={f.label}
      hint={f.hint}
      value={getColor(draft, f.path)}
      onChange={(v) => setDraft(setColor(draft, f.path, v))}
    />
  );

  return (
    <div className="space-y-4">
      {/* Header: which theme, default checkbox, revert/save */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={themeId} onValueChange={(v) => switchTheme(v as ThemeId)}>
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DIRECTIONS.map((d, i) => (
              <SelectItem key={d.id} value={d.id}>
                {i + 1}. {d.name}
                {d.id === active ? " — live" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {themeId === active && <Badge>Live</Badge>}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={makeDefault}
            // The live theme cannot be un-defaulted from here — pick another
            // theme and make THAT the default instead.
            disabled={themeId === active}
            onCheckedChange={(v) => setMakeDefault(v === true)}
          />
          Default theme
          <span className="text-xs text-muted-foreground">(what every visitor sees)</span>
        </label>
        <div className="ml-auto flex items-center gap-2">
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button
            variant="outline"
            size="sm"
            disabled={!dirty || saveMut.isPending}
            onClick={() => {
              setDraft(baseline);
              setMakeDefault(themeId === active);
            }}
          >
            <RotateCcw className="mr-1 h-4 w-4" /> Revert
          </Button>
          <Button size="sm" disabled={!dirty || saveMut.isPending} onClick={() => saveMut.mutate()}>
            <Save className="mr-1 h-4 w-4" /> {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:h-[calc(100vh-11.5rem)] lg:grid-cols-[minmax(340px,26rem)_1fr]">
        {/* Left: settings sections */}
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Fonts</CardTitle>
              <CardDescription>Headlines use the display font; everything else the body font.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <FontPicker
                label="Display font"
                value={draft.fonts.display}
                onChange={(v) => setDraft({ ...draft, fonts: { ...draft.fonts, display: v } })}
              />
              <FontPicker
                label="Body font"
                value={draft.fonts.body}
                onChange={(v) => setDraft({ ...draft, fonts: { ...draft.fonts, body: v } })}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top bar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">{TOPBAR_FIELDS.map(colorRow)}</CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Content colors</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">{CONTENT_FIELDS.map(colorRow)}</CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Highlight &amp; brand</CardTitle>
              <CardDescription>
                The brand strip gradient runs Brand → Gradient end.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {HIGHLIGHT_FIELDS.map(colorRow)}
              <div
                className="mt-2 h-2 w-full rounded-full"
                style={{
                  background: `linear-gradient(135deg, ${draft.highlight.brand}, ${draft.highlight.gradientEnd})`,
                }}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Surfaces &amp; borders</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">{SURFACE_FIELDS.map(colorRow)}</CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Shape</CardTitle>
              <CardDescription>Corner roundness across buttons, cards, and inputs.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Slider
                  value={[draft.shape.radius]}
                  min={0}
                  max={1.5}
                  step={0.125}
                  onValueChange={([v]) => setDraft({ ...draft, shape: { radius: v } })}
                  className="flex-1"
                />
                <span className="w-16 text-right font-mono text-xs text-muted-foreground">
                  {draft.shape.radius.toFixed(3)}rem
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">AI image style</CardTitle>
              <CardDescription>
                Prefixed to every AI image generation for this theme: “&lt;this style&gt; &lt;what to
                render&gt;”.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={4}
                value={draft.imagePrompt}
                onChange={(e) => setDraft({ ...draft, imagePrompt: e.target.value })}
                placeholder="e.g. High-end editorial magazine photography, cinematic, rich tones…"
              />
            </CardContent>
          </Card>
        </div>

        {/* Right: live preview — draft vars applied inline so ONLY this panel
            re-themes; the admin chrome and live site are untouched. */}
        <div
          className="min-h-0 overflow-y-auto rounded-lg border bg-background text-foreground"
          style={cssVarsForTheme(draft) as React.CSSProperties}
        >
          <ThemePreview />
        </div>
      </div>
    </div>
  );
}

function label(id: ThemeId): string {
  const i = DIRECTIONS.findIndex((d) => d.id === id);
  return `${i + 1}. ${DIRECTIONS[i]?.name ?? id}`;
}

function FontPicker({
  label: text,
  value,
  onChange,
}: {
  label: string;
  value: FontKey;
  onChange: (v: FontKey) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{text}</Label>
      <Select value={value} onValueChange={(v) => onChange(v as FontKey)}>
        <SelectTrigger style={{ fontFamily: FONT_OPTIONS[value].stack }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(FONT_OPTIONS) as FontKey[]).map((k) => (
            <SelectItem key={k} value={k} style={{ fontFamily: FONT_OPTIONS[k].stack }}>
              {FONT_OPTIONS[k].label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ColorRow({
  label: text,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm">{text}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      <Input
        value={typed ?? value}
        onChange={(e) => setTyped(e.target.value)}
        onBlur={() => {
          if (typed === null) return;
          const norm = normalizeHex(typed);
          if (norm) onChange(norm);
          setTyped(null);
        }}
        className="h-8 w-24 font-mono text-xs"
        aria-label={`${text} hex value`}
      />
      <input
        type="color"
        aria-label={text}
        title={text}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-8 shrink-0 cursor-pointer rounded border p-0"
      />
    </div>
  );
}

/**
 * Sample elements covering everything the tokens touch. Plain Tailwind
 * utilities resolve against the inline vars on the wrapper, so this stays an
 * honest preview without any extra plumbing.
 */
function ThemePreview() {
  return (
    <div className="space-y-6 p-4">
      {/* Top bar mock */}
      <div className="overflow-hidden rounded-md border">
        <div className="brand-gradient h-1 w-full" />
        <div className="flex items-center gap-3 bg-topbar px-3 py-2 text-topbar-foreground">
          <img src="/logos/original/mii-logo-final.svg" alt="" className="h-8 w-8" />
          <span className="text-sm font-semibold tracking-tight">Medical Insight</span>
          <span className="rounded-md px-2 py-1 text-sm text-topbar-foreground/70">About Us</span>
          <span className="rounded-md bg-topbar-foreground/10 px-2 py-1 text-sm text-topbar-foreground">
            Research
          </span>
          <span className="hidden rounded-md px-2 py-1 text-sm text-topbar-foreground/70 sm:inline">
            Gallery
          </span>
          <span className="ml-auto rounded-md border px-2 py-1 text-xs">Admin</span>
        </div>
      </div>

      {/* Typography */}
      <div className="space-y-2">
        <h1 className="font-display text-3xl font-bold tracking-tight">
          Strategic intelligence for the aesthetic industry
        </h1>
        <h2 className="font-display text-xl font-semibold">Market research, refined</h2>
        <p className="max-w-prose font-body text-sm leading-6">
          Trusted research, market data, and industry convening since 1985. Body copy renders in
          the body font with the content text color;{" "}
          <a className="font-medium text-link underline underline-offset-2" href="#preview">
            inline links use the link color
          </a>
          .
        </p>
        <p className="text-xs text-muted-foreground">
          Muted captions look like this — timestamps, footnotes, helper text.
        </p>
      </div>

      {/* Buttons + input */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm">
          Primary action <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="secondary">
          Secondary
        </Button>
        <Button size="sm" variant="outline">
          Outline
        </Button>
        <Badge>Badge</Badge>
        <Badge variant="secondary">Tag</Badge>
        <Input placeholder="Focus me to see the ring" className="h-8 w-52" />
      </div>

      {/* Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">A content card</CardTitle>
          <CardDescription>Card surface, card text, and border tokens.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm">
          Cards host most admin and site content; their corners follow the Shape setting.
        </CardContent>
      </Card>

      {/* Hero glass mock over the brand gradient */}
      <div className="brand-gradient relative overflow-hidden rounded-xl p-6">
        <div className="max-w-sm rounded-xl border border-white/40 bg-white/25 p-4 backdrop-blur-md">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">
            Medical Insight
          </div>
          <div className="font-display mt-1 text-lg font-semibold text-foreground">
            Hero panel over the brand gradient
          </div>
          <div className="mt-2">
            <span className="inline-block rounded-md bg-primary px-2.5 py-1 text-xs text-primary-foreground">
              Our research
            </span>
          </div>
        </div>
      </div>

      {/* CMS-ish snippet: table + blockquote */}
      <div className="space-y-3">
        <table className="w-full border-collapse overflow-hidden rounded-md text-sm">
          <thead>
            <tr className="bg-secondary text-left text-secondary-foreground">
              <th className="border px-2 py-1.5">Segment</th>
              <th className="border px-2 py-1.5">2025</th>
              <th className="border px-2 py-1.5">2030</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border px-2 py-1.5">Energy-based devices</td>
              <td className="border px-2 py-1.5">$6.2B</td>
              <td className="border px-2 py-1.5">$11.8B</td>
            </tr>
            <tr className="bg-muted/50">
              <td className="border px-2 py-1.5">Injectables</td>
              <td className="border px-2 py-1.5">$9.1B</td>
              <td className="border px-2 py-1.5">$16.4B</td>
            </tr>
          </tbody>
        </table>
        <blockquote className="border-l-4 border-primary bg-muted/40 px-3 py-2 text-sm italic">
          “The quiet authority behind the world's most beautiful brands.”
        </blockquote>
      </div>
    </div>
  );
}
